import { Link, NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LanguageSwitcher } from './LanguageSwitcher';
import { cn } from '@/lib/cn';

/**
 * Public-marketing header. Warm-paper background + hairline border. The
 * CTA points at /signup (not a cross-origin /auth/google jump) so the
 * click feels predictable and the sign-up funnel is attributable
 * client-side. Sign-in is a quieter ghost button alongside.
 */
export function SiteHeader() {
  const { t } = useTranslation();
  return (
    <header className="sticky top-0 z-40 bg-paper/80 backdrop-blur border-b border-ink-100">
      <div className="mx-auto max-w-6xl px-5 sm:px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5 font-semibold text-ink-900">
          <img src="/logo.png" alt={t('brand.name')} className="h-8 w-8 rounded-md object-contain" />
          <span className="display text-[17px] tracking-tight">{t('brand.name')}</span>
        </Link>

        <nav className="hidden md:flex items-center gap-1 text-[13.5px]">
          {[
            { to: '/pricing', label: t('nav.pricing', { defaultValue: 'Pricing' }) },
            { to: '/compare', label: t('nav.compare', { defaultValue: 'Compare' }) },
            { to: '/demo', label: t('nav.demo', { defaultValue: 'Demo' }) },
          ].map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                cn(
                  'px-3 py-1.5 rounded-md text-ink-600 hover:text-ink-900 hover:bg-ink-50 transition',
                  isActive && 'text-ink-900 bg-ink-50',
                )
              }
            >
              {item.label as string}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <LanguageSwitcher />
          <Link
            to="/login"
            className="hidden sm:inline-flex items-center px-3 py-2 rounded-lg text-[13.5px] font-medium text-ink-700 hover:text-ink-900 hover:bg-ink-50 transition"
          >
            {t('nav.login', { defaultValue: 'Sign in' })}
          </Link>
          <Link
            to="/signup"
            className="inline-flex items-center px-3.5 py-2 rounded-lg btn-press text-[13.5px]"
          >
            {t('nav.signup', { defaultValue: 'Start free' })}
          </Link>
        </div>
      </div>
    </header>
  );
}
